import React, { useEffect, useState } from "react";
import { Download, Printer } from "react-feather";
import toast from "react-hot-toast";
import { useGetCustomerQrCodeQuery, useMarkQrLabelsPrintedMutation } from "../customersApi";
import { composeQrImage } from "../qrImageUtils";
import { blobToDataUrl, openStickerWindow, writeStickers } from "../qrStickerPrint";

// Customer QR code (handoff §19.1) - GET .../qr returns a lazy-generated
// image/png (not JSON), so it goes through RTK Query as a Blob (see
// axiosBaseQuery.js's responseType passthrough) rather than a plain <img
// src="..."> - the endpoint requires the same Bearer auth as everything
// else, which an <img> tag has no way to send.
//
// This always mirrors the customer's PRIMARY location (handoff §19.6) - for
// a multi-location customer, each *other* site's own QR lives on
// CustomerSitesPanel.jsx instead, via the location-scoped .../qr endpoint.
//
// The raw QR PNG is composited (qrImageUtils.js) onto a bigger canvas with
// the customer's name and location burned into the image itself, above/
// below the code - requested because these get printed at the office and
// stuck up at the customer's premises, and a bare QR square is too easy for
// field staff to mix up between similarly-located sites. Both Download and
// Print use this composed image, not the raw scan target.
//
// Print (2026-09-17) produces the 70 x 40 mm roll sticker (qrStickerPrint.js)
// and records the print against the primary site, so the QR Stickers page
// knows this customer is done.
const CustomerQrPanel = ({ branchId, custId, custName, location, custCode, branchCode, primaryLocation }) => {
  const { data: blob, isLoading, isError } = useGetCustomerQrCodeQuery({ branchId, custId });
  const [markPrinted] = useMarkQrLabelsPrintedMutation();
  const [composedUrl, setComposedUrl] = useState(null);

  useEffect(() => {
    if (!blob) {
      setComposedUrl(null);
      return undefined;
    }
    let cancelled = false;
    composeQrImage(blob, { title: custName || `Customer ${custId}`, subtitle: location }).then((url) => {
      if (cancelled) {
        URL.revokeObjectURL(url);
        return;
      }
      setComposedUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [blob, custName, location, custId]);

  useEffect(() => () => {
    if (composedUrl) URL.revokeObjectURL(composedUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = async () => {
    if (!blob) return;
    const win = openStickerWindow(1);
    if (!win) return;
    try {
      writeStickers(win, [
        {
          dataUrl: await blobToDataUrl(blob),
          custName: (custName || `Customer ${custId}`).trim(),
          siteLabel: primaryLocation?.siteLabel,
          siteText: primaryLocation?.address || location,
          branchCode,
          custCode,
        },
      ]);
      if (primaryLocation?.locationId) markPrinted([primaryLocation.locationId]).catch(() => {});
    } catch {
      win.close();
      toast.error("Could not prepare the QR sticker");
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h5 className="mb-3">Customer QR Code</h5>
        {isLoading && <p className="text-muted mb-0">Loading...</p>}
        {isError && <p className="text-danger mb-0">Could not load the QR code.</p>}
        {composedUrl && (
          <div className="d-flex align-items-center gap-4 flex-wrap">
            <img
              src={composedUrl}
              alt={`QR code for ${custName || custId}`}
              style={{ width: 200, border: "1px solid #eee", borderRadius: 4 }}
            />
            <div className="d-flex flex-column gap-2">
              <a
                href={composedUrl}
                download={`customer-${custId}-qr.png`}
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
              >
                <Download size={14} /> Download
              </a>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                onClick={handlePrint}
              >
                <Printer size={14} /> Print
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerQrPanel;
